import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { MessageSquare, Loader2 } from 'lucide-react';
import { ChatBubble, ChatComposer, ImageLightbox } from '../chat/ChatPrimitives';

const RecruiterMessagesTab = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/recruiter/messages');
      setMessages(data);
      await api.post('/recruiter/messages/read');
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async ({ content, files }) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', content || '');
      (files || []).forEach((f) => fd.append('attachments', f));
      await api.post('/recruiter/messages/send', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
      return true;
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Error' });
      return false;
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden flex flex-col h-[70vh] min-h-[520px]">
      <div className="px-5 py-3 border-b border-[#0B5E2A]/10 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-brand-gold text-[#06401d] flex items-center justify-center font-bold text-xs">HCC</div>
        <div>
          <div className="font-semibold text-[#0B5E2A]">HCC Admissions Team</div>
          <div className="text-xs text-[#6b7e6d]">Direct chat with admissions support</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#faf9f2]" data-testid="recruiter-chat-window">
        {loading ? (<div className="text-center py-10"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#6b7e6d]" /></div>)
        : messages.length === 0 ? (
          <div className="text-center text-sm text-[#6b7e6d] py-10">
            <MessageSquare className="h-8 w-8 mx-auto brand-green opacity-60" />
            <div className="mt-2">No messages yet. Say hi to the admissions team!</div>
          </div>
        ) : messages.map((m) => (
          <ChatBubble
            key={m.id}
            message={m}
            mine={m.sender_role === 'recruiter'}
            readMark="recruiter-view"
            attachmentUrl={(mid, idx) => `/recruiter/messages/${mid}/attachments/${idx}`}
            onImageOpen={(src, name) => setLightbox({ src, name })}
          />
        ))}
        <div ref={bottomRef} />
      </div>
      <ChatComposer onSend={send} sending={sending} placeholder="Message the admissions team..." />
      {lightbox && <ImageLightbox src={lightbox.src} name={lightbox.name} onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default RecruiterMessagesTab;
